import { useState, useEffect, useMemo } from "react";
import { useUnit } from "effector-react";
import { createNewInternalList } from "@store/internal-study/list-store";
import { ComissionsShortDataT } from "@interfaces/internal-study/comissions";
import {
  $Comissions,
  fetchComissions,
} from "@store/internal-study/comissions-store";
import { $Employees, fetchEmployees } from "@store/company/employees-store";
import { EmployeeListDataT } from "@interfaces/company/employees";
import { ControlStatus } from "@interfaces/company/procedure";
import { InternalListTraineesMember } from "@interfaces/store/internal-list-store-types";
import { useCompanyId } from "@store/company/company-store";
import { useCompanyPositions } from "@lib/company-positions";
import { useDepartmentListQuery } from "@lib/departments";
import { useSearch } from "@hooks/useSearch";
import { useSearchByName } from "@hooks/useSearchByName";
import { useChoiceState } from "@hooks/useChoiceState";
import moment from "moment";
import toast from "react-hot-toast";

import {
  TextField,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
} from "@mui/material";
import { CheckboxCheckedSVG, CheckboxSVG } from "@components/svgs";
import { DropDownLabel } from "@ui/drop-down";
import { Search } from "@ui/search";
import useModal from "@modals/modal-hook";
import useStyles from "@ui/material-ui-styles";
import style from "@scss/modals/company/create-list.module.scss";
import styles from "@scss/modals/company/create-task.module.scss";
import clsx from "clsx";

const SELECT_ALL = {
  id: 0,
  title: "Выбрать все",
};

const CreateInternalListModal = () => {
  const { modalComponent, modalData, open, close } = useModal();
  const classes = useStyles();
  const companyId = useCompanyId();
  const comissions = useUnit($Comissions);
  const employees = useUnit($Employees);

  const isEditable = modalData.modeEdit ? modalData.modeEdit : true;

  const { data: departments } = useDepartmentListQuery();
  const { data: positions } = useCompanyPositions();

  const [selectedDepartment, setSelectedDepartment] = useState(SELECT_ALL.id);
  const [selectedPosition, setSelectedPosition] = useState(SELECT_ALL.id);
  const [comissionsOptions, setComissionsOptions] =
    useState<ComissionsShortDataT[]>();
  const [comissionCurrent, setComissionsCurrent] = useState<number>(
    comissionsOptions !== undefined && comissionsOptions.length
      ? comissionsOptions[0].id
      : 0
  );

  const employeesFiltered = useMemo(() => {
    return employees?.filter(
      (e) =>
        e.position.trainingPrograms?.filter(
          (t) =>
            t.category?.title === "Охрана труда" &&
            t.Pos_TrProg.location === "У работодателя"
        ).length
    );
  }, [employees]);

  const search = useSearch();
  const filteredEmployees = useSearchByName(
    search.debounced,
    employeesFiltered
  );

  const {
    selectedItems,
    areAllItemsSelected,
    selectAll,
    selectById,
    rerender,
    getIsItemSelected,
  } = useChoiceState(employeesFiltered, (employee) => employee.id);

  const departmentOptions = [
    SELECT_ALL,
    ...(departments?.map((department) => ({
      id: department.id,
      title: department.title,
    })) ?? []),
  ];
  const positionOptions = [
    SELECT_ALL,
    ...(positions
      ?.filter((position) =>
        selectedDepartment !== SELECT_ALL.id
          ? position.department.id === selectedDepartment
          : true
      )
      .map((position) => ({
        id: position.id,
        title: position.title,
      })) ?? []),
  ];

  const employeeOptions = useMemo(() => {
    const mapEmployees = (employee: EmployeeListDataT & { programs: any }) => {
      selectById(employee, false);
      const listPrograms = employee.position.trainingPrograms?.filter(
        (e) =>
          e.category?.title === "Охрана труда" &&
          e.Pos_TrProg.location === "У работодателя"
      );
      let listControl = undefined;
      if (listPrograms?.length) {
        listControl = employee.trainingControls?.filter(
          (e) =>
            e.status !== ControlStatus.NoActionRequired &&
            moment().diff(moment(e.lastTrainingDate), "days") >= 30
        );
        if (!!listPrograms?.length && listControl !== undefined)
          selectById(employee, true);
      }

      return {
        ...employee,
        programs: listPrograms?.map((e) => ({ ...e, checked: true })),
        department: employee.position.department.title,
      } as EmployeeListDataT & { programs: any };
    };

    const options = [];
    for (const employee of filteredEmployees ?? []) {
      if (
        selectedDepartment !== SELECT_ALL.id &&
        employee.position.department.id !== selectedDepartment
      ) {
        continue;
      }

      if (
        selectedPosition !== SELECT_ALL.id &&
        employee.position.id !== selectedPosition
      ) {
        continue;
      }

      options.push(
        mapEmployees(employee as EmployeeListDataT & { programs: any })
      );
    }

    return options;
  }, [filteredEmployees, selectedPosition, selectedDepartment]);

  useEffect(() => {
    if (companyId !== null) {
      fetchComissions(companyId);
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setComissionsOptions(comissions);
    if (comissions !== undefined && comissions.length) {
      setComissionsCurrent(comissions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comissions.length]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!companyId) return;

    const modalBtn = document.querySelector(
      "button.modal_btn"
    ) as HTMLButtonElement;
    modalBtn.disabled = true;

    const titleField = document.querySelector(
      'input[name="title"]'
    ) as HTMLInputElement;

    const comissionField = document.querySelector(
      'input[name="comission"]'
    ) as HTMLInputElement;

    const trainees_members = selectedItems.map((e: any) => {
      const programs = employeeOptions.find((eo) => eo.id === e.id)?.programs;
      if (programs?.filter((t: any) => t.checked).length === 0) {
        toast.error(`Выберите программы обучения у сотрудника ${e.name}`);
        return false;
      }
      return {
        id: e.id,
        email: e.email,
        inn: e.inn,
        name: e.name,
        phone: e.phone,
        snils: e.snils,
        employmentDate: e.employmentDate,
        sex: e.sex,
        position: e.position.title,
        department: e.position.department.title,
        programs: programs
          ?.filter((t: any) => t.checked)
          .map((t: any) => ({
            id: t.id,
            title: t.title,
          })),
      };
    });
    if (trainees_members.find((e) => e === false) !== undefined) {
      modalBtn.disabled = false;
      return;
    }

    createNewInternalList({
      companyId: companyId,
      title: titleField.value,
      comission: +comissionField.value,
      trainees_members: selectedItems.map((e: any) => {
        const programs = employeeOptions.find((eo) => eo.id === e.id)?.programs;
        return {
          id: e.id,
          email: e.email,
          inn: e.inn,
          name: e.name,
          phone: e.phone,
          snils: e.snils,
          employmentDate: e.employmentDate,
          sex: e.sex,
          position: e.position.title,
          department: e.position.department.title,
          programs: programs
            ?.filter((t: any) => t.checked)
            .map((t: any) => ({
              id: t.id,
              title: t.title,
            })),
        };
      }),
      cb: (err, res) => {
        if (err) {
          return console.error("При создании обучения произошла ошибка");
        }
        close();

        if (res?.data.students) {
          open("NotificationModal", {
            modalData: {
              modalTitle: "Созданы обучающиеся",
              removeIcon: true,
              textStyle: {
                textAlign: "left",
              },
              html: res?.data.students.map((e) => {
                return `<h2>${e.name}</h2><p>Логин: <b>${e.login}</b></p><p>Пароль: <b>${e.password}</b></p>`;
              }),
              text2: `Обазятельно <b>запишите</b> пароли, в дальнейшем будет доступна только смена пароля!`,
              btnText: "Записали",
            },
            onConfirm: () => {
              close();
            },
          });
        }
      },
    });
  };

  const onChangeHandleProgram = (
    checked: boolean,
    program: number,
    employee: number
  ) => {
    const employeeFind = employeeOptions?.find((e) => e.id === employee);
    if (employeeFind !== undefined) {
      const programFind = employeeFind.programs?.find(
        (e: any) => e.id === program
      );
      if (programFind !== undefined) {
        programFind.checked = checked;
      }
    }
  };

  const selectEmployee = (employee: any, checked: boolean) => {
    const employeeFind = employeeOptions?.find((e) => e === employee);
    if (employeeFind !== undefined) {
      employeeFind.programs?.forEach((e: any) => {
        e.checked = checked;
      });
    }
    selectById(employee, checked);
  };

  const selectAllPrograms = (checked: boolean) => {
    employeeOptions.forEach((e) => {
      e.programs?.forEach((p: any) => {
        p.checked = checked;
      });
    });
  };

  const handleChangeComissionCurrent = (e: any) => {
    setComissionsCurrent(+e.target.value);
  };

  return (
    <div key={modalComponent.key} className={clsx(style.create_list_modal)}>
      <p className="modal_title">{modalData.modalTitle}</p>
      <div className="underline" />
      <form className={`modal_content ${classes.root}`} onSubmit={handleSubmit}>
        <TextField
          name="title"
          label="Наименование обучения"
          variant="filled"
          required
          defaultValue={
            modalData.title ||
            `Обучение сотрудников от ${moment().format("DD.MM.YYYY")}`
          }
        />
        <TextField
          label="Комиссия"
          name="comission"
          variant="filled"
          required
          select
          value={comissionCurrent}
          onChange={handleChangeComissionCurrent}
        >
          {comissionsOptions &&
            comissionsOptions.map((comission, i) => (
              <MenuItem key={i} value={comission.id}>
                {comission.title}
              </MenuItem>
            ))}
        </TextField>
        <p className={clsx(style.title_member)}>Сотрудники:</p>
        <div className={clsx(style.members)}>
          <div className={clsx(style.members__filters)}>
            <DropDownLabel
              label="Подразделения"
              list={departmentOptions}
              type="departments"
              handlerChangeFilter={(_, value) => {
                setSelectedPosition(SELECT_ALL.id);

                if (!value) {
                  return setSelectedDepartment(SELECT_ALL.id);
                }
                setSelectedDepartment(parseInt(value));
              }}
            />
            <DropDownLabel
              label="Должности"
              list={positionOptions}
              type="positions"
              handlerChangeFilter={(_, value) => {
                if (!value) {
                  return setSelectedPosition(SELECT_ALL.id);
                }
                setSelectedPosition(parseInt(value));
              }}
            />
            <Search
              value={search.value}
              onChange={search.onChange}
              options={employees}
              getOptionLabel={(employee) => employee.name}
              placeholder="Поиск по имени"
              showDropDown={false}
              className={clsx(style.members__filters__search)}
            />
          </div>
          <div className={clsx(style.members_list)}>
            <Table>
              <TableHead>
                <TableCell width={40}>
                  <Checkbox
                    checked={areAllItemsSelected}
                    onChange={(e) => {
                      selectAll(e.target.checked);
                      selectAllPrograms(e.target.checked);
                    }}
                    color="primary"
                    icon={<CheckboxSVG />}
                    checkedIcon={<CheckboxCheckedSVG />}
                    disabled={!isEditable}
                    classes={{
                      disabled: style.members__disabledCheckbox,
                    }}
                  />
                </TableCell>
                <TableCell>Сотрудник</TableCell>
                <TableCell>Программы обучения</TableCell>
              </TableHead>
              <TableBody>
                {employeeOptions.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Checkbox
                        checked={!!getIsItemSelected(employee)}
                        onChange={(e) =>
                          selectEmployee(employee, e.target.checked)
                        }
                        color="primary"
                        icon={<CheckboxSVG />}
                        checkedIcon={<CheckboxCheckedSVG />}
                        disabled={!isEditable}
                        classes={{
                          disabled: style.members__disabledCheckbox,
                        }}
                      />
                    </TableCell>
                    <TableCell>{employee.name}</TableCell>
                    <TableCell>
                      {employee.programs &&
                        employee.programs.map((p: any, i: number) => (
                          <FormControlLabel
                            key={i}
                            control={
                              <Checkbox
                                checked={p.checked}
                                name="program"
                                color="primary"
                                icon={<CheckboxSVG />}
                                checkedIcon={<CheckboxCheckedSVG />}
                                onChange={(event) => {
                                  onChangeHandleProgram(
                                    event.target.checked,
                                    p.id,
                                    employee.id
                                  );
                                  rerender(employee);
                                }}
                              />
                            }
                            label={p.title}
                            labelPlacement="end"
                            style={
                              employee.programs?.length !== undefined &&
                              i !== employee.programs?.length - 1
                                ? {
                                    borderBottom: "1px solid #e0e0e0",
                                    marginBottom: 5,
                                    paddingBottom: 5,
                                  }
                                : {}
                            }
                          />
                        ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <button type="submit" className="modal_btn">
          {modalComponent.btnText}
        </button>
      </form>
    </div>
  );
};

export default CreateInternalListModal;
